import api from './api';

// Schedule assignment interfaces
export type ScheduleAssignmentStatus = 'scheduled' | 'playing' | 'completed' | 'skipped' | 'failed';

export interface ScheduleAssignment {
  id: string;
  schedule_template_id: string;
  channel_id: string;
  start_time: string;
  end_time: string;
  status: ScheduleAssignmentStatus;
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  current_video_index: number;
  error_message?: string | null;
  retry_count: number;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleAssignmentWithDetails extends ScheduleAssignment {
  schedule_name?: string;
  template_name?: string; // Alias for schedule_name
  schedule_description?: string;
  schedule_total_duration?: number;
  schedule_video_count?: number;
  channel_name?: string;
}

export interface CreateScheduleAssignmentInput {
  schedule_template_id: string;
  channel_id: string;
  start_time: string; // ISO 8601 format
  notes?: string;
}

export interface UpdateScheduleAssignmentInput {
  start_time?: string;
  status?: ScheduleAssignmentStatus;
  notes?: string;
}

export interface ListScheduleAssignmentsParams {
  channel_id?: string;
  status?: ScheduleAssignmentStatus;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
  sort_by?: 'start_time' | 'created_at';
  sort_order?: 'asc' | 'desc';
}

export interface CheckConflictInput {
  channel_id: string;
  schedule_template_id: string;
  start_time: string;
  exclude_assignment_id?: string;
}

export interface ConflictResult {
  has_conflict: boolean;
  conflicting_assignment?: ScheduleAssignment;
}

export interface ScheduleAssignmentStats {
  total: number;
  scheduled: number;
  playing: number;
  completed: number;
  failed: number;
}

const scheduleAssignmentService = {
  /**
   * Create a new schedule assignment
   */
  create: async (data: CreateScheduleAssignmentInput): Promise<ScheduleAssignment> => {
    const response = await api.post('/schedule-assignments', data);
    return response.data.data;
  },

  /**
   * List schedule assignments
   */
  list: async (params?: ListScheduleAssignmentsParams) => {
    const response = await api.get('/schedule-assignments', { params });
    return {
      assignments: response.data.data as ScheduleAssignmentWithDetails[],
      pagination: response.data.pagination,
    };
  },

  /**
   * Get schedule assignment by ID
   */
  get: async (id: string): Promise<ScheduleAssignmentWithDetails> => {
    const response = await api.get(`/schedule-assignments/${id}`);
    return response.data.data;
  },

  /**
   * Update schedule assignment
   */
  update: async (id: string, data: UpdateScheduleAssignmentInput): Promise<ScheduleAssignment> => {
    const response = await api.put(`/schedule-assignments/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete schedule assignment
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/schedule-assignments/${id}`);
  },

  /**
   * Check for schedule conflicts
   */
  checkConflict: async (data: CheckConflictInput): Promise<ConflictResult> => {
    const response = await api.post('/schedule-assignments/check-conflict', data);
    return response.data.data;
  },

  /**
   * Get last end time for a channel on a specific date (for snap-to-end)
   */
  getLastEndTime: async (channelId: string, date: string): Promise<string | null> => {
    const response = await api.get('/schedule-assignments/last-end-time', {
      params: { channel_id: channelId, date },
    });
    return response.data.data.last_end_time;
  },

  /**
   * Get current playing assignment for a channel
   */
  getCurrentPlaying: async (channelId: string): Promise<ScheduleAssignmentWithDetails | null> => {
    const response = await api.get(`/schedule-assignments/current-playing/${channelId}`);
    return response.data.data;
  },

  /**
   * Get upcoming assignments for a channel
   */
  getUpcoming: async (channelId: string, limit?: number): Promise<ScheduleAssignmentWithDetails[]> => {
    const response = await api.get(`/schedule-assignments/upcoming/${channelId}`, {
      params: { limit },
    });
    return response.data.data;
  },

  /**
   * Update assignment status
   */
  updateStatus: async (id: string, status: ScheduleAssignmentStatus, errorMessage?: string): Promise<ScheduleAssignment> => {
    const response = await api.put(`/schedule-assignments/${id}/status`, {
      status,
      error_message: errorMessage,
    });
    return response.data.data;
  },

  /**
   * Mark assignment as playing
   */
  markAsPlaying: async (id: string): Promise<ScheduleAssignment> => {
    return scheduleAssignmentService.updateStatus(id, 'playing');
  },

  /**
   * Mark assignment as completed
   */
  markAsCompleted: async (id: string): Promise<ScheduleAssignment> => {
    return scheduleAssignmentService.updateStatus(id, 'completed');
  },

  /**
   * Mark assignment as failed
   */
  markAsFailed: async (id: string, errorMessage: string): Promise<ScheduleAssignment> => {
    return scheduleAssignmentService.updateStatus(id, 'failed', errorMessage);
  },

  /**
   * Mark assignment as skipped
   */
  markAsSkipped: async (id: string): Promise<ScheduleAssignment> => {
    return scheduleAssignmentService.updateStatus(id, 'skipped');
  },

  /**
   * Bulk delete assignments
   */
  bulkDelete: async (ids: string[]): Promise<number> => {
    const response = await api.post('/schedule-assignments/bulk-delete', { ids });
    return response.data.data.deletedCount;
  },

  /**
   * Get schedule assignment statistics
   */
  getStats: async (channelId?: string): Promise<ScheduleAssignmentStats> => {
    const response = await api.get('/schedule-assignments/stats', {
      params: channelId ? { channel_id: channelId } : {},
    });
    return response.data.data;
  },

  /**
   * Reschedule an assignment (update start_time)
   */
  reschedule: async (id: string, newStartTime: string): Promise<ScheduleAssignment> => {
    return scheduleAssignmentService.update(id, { start_time: newStartTime });
  },

  // Aliases for consistency with SchedulesPage expectations
  listAssignments: async (params?: ListAssignmentsParams) => {
    return scheduleAssignmentService.list(params);
  },

  createAssignment: async (data: CreateAssignmentInput): Promise<ScheduleAssignment> => {
    return scheduleAssignmentService.create(data);
  },

  deleteAssignment: async (id: string): Promise<void> => {
    return scheduleAssignmentService.delete(id);
  },
};

// Type aliases for consistency
export type ListAssignmentsParams = ListScheduleAssignmentsParams;
export type CreateAssignmentInput = CreateScheduleAssignmentInput;
export type AssignmentStatus = ScheduleAssignmentStatus;

export default scheduleAssignmentService;
